import services from 'services/module';
import {Strings} from 'Strings';
import {Utils} from '../underscore-extensions';
import {LocalStorageSvc} from './LocalStorageSvc';

export class AnalyticSvc {
    private mixpanelEnabled = !options.isLocal;
    private shouldTrackVisits: boolean;
    private activateKeyName = 'analytic_track_activate_page_';

    constructor(private $location: ng.ILocationService,
                private $state: ng.ui.IStateService,
                private $rootScope: ng.IRootScopeService,
                private $log: ng.ILogService,
                private LocalStorageSvc: LocalStorageSvc) {
        const visit = _.once(() => this.trackVisit());

        $rootScope.$on('$stateChangeSuccess', () => {
            visit();
            this.registerCommonProperties();
        });
    }

    upgradeShown(action: anm.UpgradeAction, plan = this.getDefaultPlan()) {
        const opt = {
            ...this.getPlan(),
            [`${this.getProduct()} Plan`]: plan,
            Product: this.getProduct(),
        };

        this.trackMixpanel(`${Strings.capitalize(action)} Shown`, opt);
    }

    changeBillingPeriod(oldPeriod: string, newPeriod: string) {
        const opt = {
            ...this.getPlan(),
            'Old Period': oldPeriod,
            'New Period': newPeriod
        };

        this.trackMixpanel('Change Billing Period', opt);
    }

    changeSubscription(action: anm.UpgradeAction,
                       oldPlan: string,
                       oldPeriod: string,
                       toPlan = this.getDefaultPlan(),
                       toPeriod?: string,
                       chargedAmount?: number,
                       coupon?: string) {
        this.mpChangeSubscription(
            action,
            oldPlan,
            oldPeriod,
            toPlan,
            toPeriod,
            chargedAmount,
            coupon
        );

        this.fbqChangeSubscription(
            oldPlan,
            toPlan,
            toPeriod
        )
    }

    signUpSubmit() {
        this.gaSignUpEvent('Submit');
        this.gaProductSignUpEvent();
        this.getMixPanel(mixpanel => {
            mixpanel.track('Sign Up Submit', {Provider: 'EMAIL'});
        });
    }

    signUpActivate(hash: string) {
        const currentHash = this.LocalStorageSvc.get(this.activateKeyName);

        if (hash && hash !== currentHash) {
            this.gaSignUpEvent('Activate');
            this.getMixPanel(mixpanel => mixpanel.track('Sign Up Activation', {Provider: 'EMAIL'}));
            this.LocalStorageSvc.set(this.activateKeyName, hash);
        }
    }

    signUpComplete() {
        this.gaSignUpEvent('Complete');
        this.fbPixelSignUpComplete();
        this.trackSignUp('Email');
    }

    signUpProfileComplete(provider: anm.SignUpProvider) {
        this.gaSignUpEvent('ProfileComplete', provider);
    }

    signUpSocialSubmit(provider: anm.SignUpProvider) {
        this.gaSignUpEvent('Submit', provider);
    }

    signUpSocialComplete(provider: anm.SignUpProvider) {
        this.gaSignUpEvent('Complete', provider);
        this.trackSignUp(provider);
    }

    login(user: anm.User) {
        this.gaSetUserId(user.userId);
        this.trackLoginMixpanel();
    }

    logout() {
        this.gaSetUserId(null);
        this.mixpanelLogout();
    }

    visitPage(pageName: string, additionalParams = {}) {
        const params = {Product: this.getProduct(), ...additionalParams};
        this.trackMixpanel(`Visit ${pageName}`, params);
    }

    changePage() {
        if (!window.ga) return;

        window.ga('set', 'page', this.$location.url());
        window.ga('send', 'pageview');
    }

    getPlan() {
        const {wave, studio} = options.user.subscriptionDetails;
        return {
            'WAVE Plan': wave.name,
            'STUDIO Plan': studio.name
        }
    }

    private fbPixelSignUpComplete() {
        const {
                  industry,
                  goals,
                  occupation
              }      = options.user.meta;
        const params = {
            product: this.getProduct(),
            industry: industry,
            goals: goals
        };

        if (occupation) {
            params['occupation'] = occupation
        }

        window.fbq && window.fbq('track', 'CompleteRegistration', params);
    }

    private fbqChangeSubscription(oldPlan: string,
                                  toPlan = this.getDefaultPlan(),
                                  toPeriod?: string) {
        window.fbq && window.fbq('track', 'ChangePlan', {
            product: this.getProduct(),
            new_plan: toPlan,
            old_plan: oldPlan,
            period: toPeriod
        });
    }

    private mpChangeSubscription(action: anm.UpgradeAction,
                                 oldPlan: string,
                                 oldPeriod: string,
                                 toPlan = this.getDefaultPlan(),
                                 toPeriod?: string,
                                 chargedAmount?: number,
                                 coupon?: string) {
        this.getMixPanel(mixpanel => {
            const params = {
                ...this.getPlan(),
                [`${this.getProduct()} Plan`]: toPlan,
                [`${this.getProduct()} Old Plan`]: oldPlan,
                'Old Period': oldPeriod,
                Product: this.getProduct(),
                Period: toPeriod
            };

            if (chargedAmount && action === 'upgrade') {
                params['Charged'] = chargedAmount.toString();
                mixpanel.people.track_charge(chargedAmount);
            }

            if (coupon) params['Coupon'] = coupon;

            const eventName = Strings.capitalize(action) + 'd';
            this.trackMixpanel(eventName, params);
        });
    }

    private trackVisit() {
        return this.getMixPanel(mixpanel => {
            const canVisit         = !['activate'].some(s => this.$state.current.name.includes(s));
            this.shouldTrackVisits = this.shouldTrackVisits || window.performance.navigation.type === 0;

            if (!this.shouldTrackVisits || !canVisit) return;
            this.shouldTrackVisits = false;

            if (options.user.anonymous) {
                mixpanel.track('Guest Visit', {});
            } else {
                mixpanel.identify(options.user.userIdAsString);
                mixpanel.track('Visit', {});
            }
        });
    }

    private mixpanelLogout() {
        return this.getMixPanel(mixpanel => {
            mixpanel.reset();
            this.shouldTrackVisits = true;
        });
    }

    private getMixPanel(func: (m: Mixpanel) => void): void {
        return this.mixpanelEnabled && window.mixpanel && func(window.mixpanel)
    }

    private registerCommonProperties() {
        const props = {
            Place: 'Website',
            Product: this.getProduct()
        };
        return this.getMixPanel(mixpanel => mixpanel.register(props));
    }

    private registerUserProperties() {
        this.getMixPanel(mixpanel => {
            const props = {
                ...this.getPlan(),
                '$name': options.user.displayName,
                Username: options.user.username,
                Product: this.getProduct()
            };

            if (options.user.email) {
                props['$email'] = options.user.email;
            }

            mixpanel.register(props);
        });
    }

    private userInfoMixpanel() {
        const displayName = options.user.displayName,
              idx         = displayName.indexOf(' ');

        const props = {
            ...this.getPlan(),
            '$name': displayName,
            '$first_name': idx >= 0 ? displayName.substring(0, idx) : displayName,
            '$last_name': idx >= 0 ? displayName.substring(idx + 1) : '',
            userId: options.user.userIdAsString,
            Username: options.user.username,
            Industry: options.user.meta.industry,
            Occupation: options.user.meta.occupation,
            Goals: options.user.meta.goals
        };

        if (options.user.email) {
            props['$email'] = options.user.email;
        }

        return props;
    }

    private trackMixpanel(eventName: string, opts?: {[index: string]: any}) {
        return this.getMixPanel(m => {
            m.track(eventName, opts);
            this.$log.debug('Mixpanel track: ', ...m[0]);

            return m;
        });
    }

    private trackLoginMixpanel() {
        return this.getMixPanel(mixpanel => {
            this.shouldTrackVisits = true;
            this.trackVisit();
            mixpanel.people.set(this.userInfoMixpanel());
            this.registerUserProperties();
        });
    }

    private isAllowedMethod(provider: string) {
        return !_.isUndefined(provider) &&
            ['google', 'facebook', 'email', 'twitter'].indexOf(provider.toLowerCase()) !== 1;
    }

    private trackSignUp(provider: anm.SignUpProvider) {
        if (this.isAllowedMethod(provider)) {
            this.getMixPanel(mixpanel => {
                mixpanel.alias(options.user.userIdAsString);
                mixpanel.people.set_once(_.extend({
                    '$created': new Date().toISOString(),
                    Provider: provider,
                    Place: 'Website',
                    Product: options.user.meta.product
                }, this.userInfoMixpanel()));
                this.registerUserProperties();
                mixpanel.track('Sign Up', {Provider: Strings.capitalize(provider)});
            });
        }
    }

    private getDefaultPlan() {
        return this.getProduct() === 'WAVE' ? 'WaveFree' : 'Free';
    }

    private getProduct() {
        return Utils.getProduct();
    }

    private gaSetUserId(userId: string) {
        window.ga && window.ga('set', 'userId', userId);

    }

    private gaSignUpEvent(eventName: string, provider = 'Email') {
        window.ga && window.ga('send', 'event', 'SignUp', eventName, Strings.capitalize(provider.toLowerCase()));
    }

    private gaProductSignUpEvent() {
        window.ga && window.ga('send', 'event', this.getProduct(), 'Sign Up');
    }
}

services.service('analyticSvc', AnalyticSvc);



// WEBPACK FOOTER //
// ./app/assets/javascripts/services/AnalyticSvc.ts